import { observationKey } from '@/services/checklist/checklistObservations';
import ChecklistMediaObservation from '@/components/checklist/ChecklistMediaObservation';
import React, { useMemo, useRef, useEffect } from 'react';
import { SectionList, Text, TextInput, StyleSheet, View, ViewToken } from 'react-native';
// @ts-ignore
import { groupBy } from 'lodash';
import theme from '@/config/theme';
import FormattedInput from '@/components/FormattedInput';
import ChecklistCEPField from '@/components/checklist/ChecklistCEPField';
import ChecklistDateTimeField from '@/components/checklist/ChecklistDateTimeField';
import ChecklistReadyMessageField from '@/components/checklist/ChecklistReadyMessageField';
import DevChecklistPhotoField from '@/components/checklist/dev/DevChecklistPhotoField';
import DevChecklistSignatureField from '@/components/checklist/dev/DevChecklistSignatureField';
import DevChecklistMarkField from '@/components/checklist/dev/DevChecklistMarkField';
import DevChecklistDropdownField from '@/components/checklist/dev/DevChecklistDropdownField';

interface ChecklistField {
  id: string;
  grupo: string;
  nome: string;
  tipo: string;
  ordem: number;
}

interface DevChecklistFieldsFormProps {
  checklistId: string;
  empresaId: string;
  template: ChecklistField[];
  draft: Record<string, string>;
  onChange: (campoId: string, value: string, options?: { immediate?: boolean }) => void;
  onActiveGroupChange?: (group: string) => void;
  readOnly?: boolean;
}

type ChecklistSection = { title: string; data: ChecklistField[] };

const TextField: React.FC<{ field: ChecklistField; value: string; onChange: (val: string) => void }> = React.memo(
  ({ field, value, onChange }) => (
    <View style={styles.itemContainer}>
      <Text style={styles.label}>{field.nome}</Text>
      <TextInput
        style={styles.input}
        value={value}
        onChangeText={onChange}
        placeholder={field.nome}
        placeholderTextColor={theme.colors.text.tertiary}
        autoCorrect={false}
        autoCapitalize="sentences"
      />
    </View>
  )
);

const NumberField: React.FC<{ field: ChecklistField; value: string; onChange: (val: string) => void }> = React.memo(
  ({ field, value, onChange }) => (
    <View style={styles.itemContainer}>
      <Text style={styles.label}>{field.nome}</Text>
      <FormattedInput
        style={styles.input}
        value={value}
        onChangeText={onChange}
        type="number"
        placeholder="Digite apenas números"
      />
    </View>
  )
);

export const DevChecklistFieldsForm = React.forwardRef<unknown, DevChecklistFieldsFormProps>(({
  checklistId,
  empresaId,
  template,
  draft,
  onChange,
  onActiveGroupChange,
  readOnly = false,
}, _ref) => {
  const changeIfWritable = (
    fieldId: string,
    value: string,
    options?: { immediate?: boolean }
  ) => {
    if (!readOnly) onChange(fieldId, value, options);
  };

  const orderedFields = useMemo<ChecklistField[]>(() => template, [template]);
  const sections: ChecklistSection[] = useMemo(
    () => Object.entries(groupBy(orderedFields, 'grupo')).map(([title, data]) => ({
      title,
      data: data as ChecklistField[],
    })),
    [orderedFields]
  );

  const sectionListRef = useRef<SectionList<ChecklistField, ChecklistSection>>(null);
  const lastReportedGroupRef = useRef<string | null>(null);
  const viewabilityConfig = useRef({
    minimumViewTime: 120,
    itemVisiblePercentThreshold: 50,
  });
  const handleViewableItemsChanged = useRef(({ viewableItems }: { viewableItems: ViewToken[] }) => {
    if (!viewableItems.length) return;
    const headerToken = viewableItems.find(token => token.index === null && (token.section as ChecklistSection | undefined)?.title);
    const fallbackToken = viewableItems.find(token => (token.section as ChecklistSection | undefined)?.title);
    const nextGroup = headerToken?.section
      ? (headerToken.section as ChecklistSection).title
      : fallbackToken?.section
        ? (fallbackToken.section as ChecklistSection).title
        : undefined;

    if (!nextGroup) return;
    if (!sections.some(section => section.title === nextGroup)) return;
    if (nextGroup === lastReportedGroupRef.current) return;
    lastReportedGroupRef.current = nextGroup;
    onActiveGroupChange?.(nextGroup);
  });

  useEffect(() => {
    if (!sections.length) return;
    const firstGroup = sections[0]?.title;
    if (!firstGroup) return;

    const currentGroup = lastReportedGroupRef.current;
    const groupAindaExiste = currentGroup
      ? sections.some(section => section.title === currentGroup)
      : false;

    if (currentGroup && groupAindaExiste) return;
    lastReportedGroupRef.current = firstGroup;
    onActiveGroupChange?.(firstGroup);
  }, [onActiveGroupChange, sections]);

  const renderItem = ({ item }: { item: ChecklistField }) => {
    if (item.tipo.startsWith('dropdown')) {
      return (
        <DevChecklistDropdownField
          tipo={item.tipo}
          label={item.nome}
          value={draft[item.id] ?? ''}
          onChange={(val) => changeIfWritable(item.id, val)}
        />
      );
    }

    switch (item.tipo) {
      case 'text':
        return (
          <TextField
            field={item}
            value={draft[item.id] ?? ''}
            onChange={(val) => changeIfWritable(item.id, val)}
          />
        );
      case 'foto':
        return (
          <DevChecklistPhotoField
            empresaId={empresaId}
            checklistId={checklistId}
            fieldId={item.id}
            value={draft[item.id]}
            readOnly={readOnly}
            onChange={(val) => changeIfWritable(item.id, val, { immediate: true })}
          />
        );
      case 'assinatura':
        return (
          <View style={styles.itemContainer}>
            <Text style={styles.label}>{item.nome}</Text>
            <DevChecklistSignatureField
              empresaId={empresaId}
              checklistId={checklistId}
              fieldId={item.id}
              value={draft[item.id]}
              readOnly={readOnly}
              onChange={(val) => changeIfWritable(item.id, val, { immediate: true })}
            />
          </View>
        );
      case 'marcação':
        return (
          <View style={styles.itemContainer}>
            <Text style={styles.label}>{item.nome}</Text>
            <DevChecklistMarkField
              empresaId={empresaId}
              checklistId={checklistId}
              fieldId={item.id}
              value={draft[item.id]}
              readOnly={readOnly}
              onChange={(val) => changeIfWritable(item.id, val, { immediate: true })}
            />
          </View>
        );
      case 'cep1': {
        const sameGroup = template.filter(f => f.grupo === item.grupo);
        const findId = (t: string) => sameGroup.find(f => f.tipo === t)?.id;

        const enderecoId = findId('cep2');
        const bairroId = findId('cep4');
        const cidadeId = findId('cep5');
        const ufId = findId('cep6');

        return (
          <ChecklistCEPField
            field={item}
            value={draft[item.id] ?? ''}
            onChangeSelf={(val) => changeIfWritable(item.id, val)}
            updateField={(fieldId, val) => changeIfWritable(fieldId, val)}
            enderecoId={enderecoId}
            bairroId={bairroId}
            cidadeId={cidadeId}
            ufId={ufId}
          />
        );
      }
      case 'cep2':
      case 'cep3':
      case 'cep4':
      case 'cep5':
      case 'cep6':
        return (
          <TextField
            field={item}
            value={draft[item.id] ?? ''}
            onChange={(val) => changeIfWritable(item.id, val)}
          />
        );
      case 'timestamp with time zone':
        return (
          <ChecklistDateTimeField
            field={item}
            value={draft[item.id] ?? ''}
            onChange={(val) => changeIfWritable(item.id, val)}
          />
        );
      case 'number':
        return (
          <NumberField
            field={item}
            value={draft[item.id] ?? ''}
            onChange={(val) => changeIfWritable(item.id, val)}
          />
        );
      case 'mensagem pronta':
        return (
          <View style={styles.itemContainer}>
            <ChecklistReadyMessageField
              field={item}
              value={draft[item.id]}
              onChange={(val) => changeIfWritable(item.id, val)}
            />
          </View>
        );
      default:
        return null;
    }
  };

  return (
    <SectionList
      ref={sectionListRef}
      sections={sections}
      keyExtractor={(f: ChecklistField) => f.id}
      extraData={draft}
      renderSectionHeader={({ section }) => (
        <Text style={styles.header}>{section.title}</Text>
      )}
      renderItem={({ item }) => <View>
        {renderItem({ item })}
        <ChecklistMediaObservation fieldId={item.id} label={item.nome}
          value={draft[observationKey(item.id)] ?? ''} editable={false} onChange={() => {}} />
      </View>}
      contentContainerStyle={styles.container}
      onViewableItemsChanged={handleViewableItemsChanged.current}
      viewabilityConfig={viewabilityConfig.current}
    />
  );
});

DevChecklistFieldsForm.displayName = 'DevChecklistFieldsForm';

const styles = StyleSheet.create({
  container: {
    padding: theme.spacing.md,
    backgroundColor: theme.colors.background.primary,
  },
  header: {
    fontWeight: 'bold',
    fontSize: theme.fontSizes.md,
    marginTop: theme.spacing.md,
    marginBottom: theme.spacing.sm,
    color: theme.colors.text.primary,
  },
  itemContainer: {
    marginBottom: 12,
    padding: 0,
  },
  label: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.secondary,
    marginBottom: theme.spacing.xs,
  },
  input: {
    backgroundColor: theme.colors.background.primary,
    borderRadius: theme.borderRadius.sm,
    borderWidth: 1,
    borderColor: theme.colors.border.light,
    padding: theme.spacing.sm,
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.primary,
  },
});
